import { useEffect, useRef } from 'react';
import type { Histogram } from '../../engine/histogram';
import { useT } from '../../i18n';
import { cssVar } from '../../app/theme';

/** R, G, B and luma histogram (§11.9), drawn on a small 2D canvas. Colour plus a luma outline. */
export function HistogramView({ hist }: { hist: Histogram | null }) {
  const t = useT();
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d')!;
    const W = c.width,
      H = c.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = cssVar('--chart-bg');
    ctx.fillRect(0, 0, W, H);
    if (!hist || !hist.count) return;
    let max = 1;
    for (const ch of [hist.r, hist.g, hist.b]) for (let i = 1; i < 255; i++) max = Math.max(max, ch[i]!);
    ctx.globalCompositeOperation = 'lighter';
    const draw = (ch: Uint32Array, color: string) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(0, H);
      for (let i = 0; i < 256; i++) ctx.lineTo((i / 255) * W, H - Math.min(1, ch[i]! / max) * H);
      ctx.lineTo(W, H);
      ctx.fill();
    };
    draw(hist.r, cssVar('--chart-r'));
    draw(hist.g, cssVar('--chart-g'));
    draw(hist.b, cssVar('--chart-b'));
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = cssVar('--chart-luma');
    ctx.beginPath();
    for (let i = 0; i < 256; i++) ctx.lineTo((i / 255) * W, H - Math.min(1, hist.l[i]! / max) * H);
    ctx.stroke();
  }, [hist]);
  return (
    <canvas
      ref={ref}
      width={276}
      height={80}
      role="img"
      aria-label={t('histogram')}
      style={{ display: 'block', width: '100%', height: 80 }}
    />
  );
}
